import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canManageNotificationTemplates } from "@/lib/permissions";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Plus, Save, Trash2, X, Search, HelpCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";

// Only the super_admin should be able to edit help.
const canEdit = (user) => user?.role === "super_admin";

const TABS = [
  { key: "introduction", label: "Introduction" },
  { key: "glossary", label: "Glossary" },
  { key: "how_to", label: "How-To" },
];

export default function Help() {
  const { user } = useAuth();
  const [sections, setSections] = useState(null);
  const [tab, setTab] = useState("introduction");
  const [editing, setEditing] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  const [draftEntries, setDraftEntries] = useState([]);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/help");
    setSections(data);
  };

  useEffect(() => { load(); }, []);

  const startEdit = () => {
    const sec = sections?.[tab] || {};
    if (tab === "glossary") {
      setDraftEntries((sec.entries || []).map((e) => ({ ...e })));
    } else {
      setDraftBody(sec.body || "");
    }
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraftBody("");
    setDraftEntries([]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = tab === "glossary" ? { entries: draftEntries } : { body: draftBody };
      await api.put(`/help/${tab}`, payload);
      toast.success("Saved");
      await load();
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const glossaryEntries = useMemo(() => {
    const entries = sections?.glossary?.entries || [];
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => e.term.toLowerCase().includes(q) || (e.definition || "").toLowerCase().includes(q));
  }, [sections, query]);

  if (!sections) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2" data-testid="help-page-title">
            <HelpCircle className="w-6 h-6 text-blue-400" /> Help
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Everything you need to work confidently in ZigZagX.</p>
        </div>
        {canEdit(user) && !editing && (
          <Button
            onClick={startEdit}
            data-testid="help-edit-btn"
            size="sm"
            variant="outline"
            className="rounded-sm border-zinc-800 bg-zinc-950 hover:bg-zinc-900 gap-1.5"
          >
            <Pencil className="w-3.5 h-3.5" /> Edit
          </Button>
        )}
        {editing && (
          <div className="flex items-center gap-2">
            <Button onClick={cancelEdit} data-testid="help-cancel-btn" size="sm" variant="outline" className="rounded-sm border-zinc-800 bg-zinc-950 hover:bg-zinc-900 gap-1.5">
              <X className="w-3.5 h-3.5" /> Cancel
            </Button>
            <Button onClick={save} disabled={saving} data-testid="help-save-btn" size="sm" className="rounded-sm bg-blue-600 hover:bg-blue-500 gap-1.5">
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={(v) => { if (editing) cancelEdit(); setTab(v); }}>
        <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm p-0.5 mb-6">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`help-tab-${t.key}`} className="rounded-sm data-[state=active]:bg-zinc-800 text-xs uppercase tracking-wider font-mono">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="introduction">
          {editing ? (
            <Textarea
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              data-testid="help-body-editor"
              className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-sm min-h-[500px]"
            />
          ) : (
            <MarkdownView body={sections.introduction?.body || ""} />
          )}
        </TabsContent>

        <TabsContent value="glossary">
          {editing ? (
            <GlossaryEditor entries={draftEntries} onChange={setDraftEntries} />
          ) : (
            <>
              <div className="relative mb-4 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search terms and definitions"
                  data-testid="glossary-search"
                  className="bg-zinc-950 border-zinc-800 rounded-sm pl-9 font-mono"
                />
              </div>
              <dl className="divide-y divide-zinc-900 border border-zinc-900 rounded-sm">
                {glossaryEntries.length === 0 && (
                  <div className="p-6 text-sm text-zinc-500 font-mono">No matching terms.</div>
                )}
                {glossaryEntries.map((e) => (
                  <div key={e.term} className="p-4 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 md:gap-6" data-testid={`glossary-row-${e.term}`}>
                    <dt className="font-heading text-sm font-semibold text-blue-400 uppercase tracking-wider">{e.term}</dt>
                    <dd className="text-sm text-zinc-300 leading-relaxed">{e.definition}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </TabsContent>

        <TabsContent value="how_to">
          {editing ? (
            <Textarea
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              data-testid="help-body-editor"
              className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-sm min-h-[500px]"
            />
          ) : (
            <MarkdownView body={sections.how_to?.body || ""} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MarkdownView({ body }) {
  if (!body) return <p className="text-sm text-zinc-500 italic">Nothing here yet.</p>;
  return (
    <article className="max-w-none text-zinc-300 text-sm leading-relaxed space-y-3" data-testid="help-markdown">
      <ReactMarkdown
        components={{
          h1: ({ node, ...p }) => <h1 className="font-heading text-2xl font-semibold tracking-tight text-zinc-100 mt-2 mb-4" {...p} />,
          h2: ({ node, ...p }) => <h2 className="font-heading text-xl font-semibold tracking-tight text-zinc-100 mt-8 mb-3 flex items-center gap-2" {...p} />,
          h3: ({ node, ...p }) => <h3 className="font-heading text-base font-semibold text-zinc-100 mt-6 mb-2" {...p} />,
          p: ({ node, ...p }) => <p className="text-sm text-zinc-300 leading-relaxed" {...p} />,
          strong: ({ node, ...p }) => <strong className="text-zinc-100 font-semibold" {...p} />,
          a: ({ node, ...p }) => <a className="text-blue-400 hover:text-blue-300 underline underline-offset-2" {...p} />,
          code: ({ node, ...p }) => <code className="text-blue-300 bg-zinc-900 px-1.5 py-0.5 rounded-sm font-mono text-xs" {...p} />,
          ul: ({ node, ...p }) => <ul className="list-disc pl-6 space-y-1.5 marker:text-zinc-600" {...p} />,
          ol: ({ node, ...p }) => <ol className="list-decimal pl-6 space-y-1.5 marker:text-zinc-500 marker:font-mono" {...p} />,
          li: ({ node, ...p }) => <li className="text-sm text-zinc-300 leading-relaxed" {...p} />,
          hr: () => <hr className="border-zinc-800 my-6" />,
        }}
      >{body}</ReactMarkdown>
    </article>
  );
}

function GlossaryEditor({ entries, onChange }) {
  const setEntry = (i, patch) => {
    const next = entries.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(entries.filter((_, idx) => idx !== i));
  const add = () => onChange([...entries, { term: "", definition: "" }]);
  return (
    <div className="space-y-3" data-testid="glossary-editor">
      {entries.map((e, i) => (
        <div key={i} className="grid grid-cols-[220px_1fr_auto] gap-3 items-start border border-zinc-900 rounded-sm p-3">
          <Input
            value={e.term}
            onChange={(ev) => setEntry(i, { term: ev.target.value })}
            placeholder="Term"
            className="bg-zinc-950 border-zinc-800 rounded-sm font-mono uppercase text-sm"
          />
          <Textarea
            value={e.definition}
            onChange={(ev) => setEntry(i, { definition: ev.target.value })}
            placeholder="Definition"
            className="bg-zinc-950 border-zinc-800 rounded-sm text-sm min-h-[64px]"
          />
          <Button onClick={() => remove(i)} variant="ghost" size="sm" className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 rounded-sm">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button onClick={add} variant="outline" size="sm" className="rounded-sm border-zinc-800 bg-zinc-950 hover:bg-zinc-900 gap-1.5" data-testid="glossary-add-row">
        <Plus className="w-3.5 h-3.5" /> Add Term
      </Button>
    </div>
  );
}
